import { useContext } from 'react';
import { ClickAwayListener, Tooltip, Typography } from '@mui/material';
import styled from 'styled-components';
import { Flex, FlexColumn } from '../Flex';
import Button from '../Button';
import { ReactComponent as GotoIcon } from '../../assets/16x16/goto.svg';

const GotoIconStyled = styled(GotoIcon)`
  path {
    stroke: white;
  }
`;

const StyledTooltipContent = styled(FlexColumn)`
  gap: 16px;
  width: 280px;
  padding: 16px;
  background: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.primary};
  border: 1px solid #ebedef;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
`;

const TooltipContent = ({
  description,
  link,
}: {
  description?: string;
  link: string;
}) => (
  <StyledTooltipContent>
    <Typography fontSize={'14px'}>{description}</Typography>
    <Flex>
      <Button
        endIcon={<GotoIconStyled />}
        onClick={() => window.open(link, '_blank')}
      >
        Learn more
      </Button>
    </Flex>
  </StyledTooltipContent>
);

type Props = {
  description?: string;
  link: string;
  children: any;
  tooltipId: string;
  TooltipContext: React.Context<{
    openTooltip: string | null;
    setOpenTooltip: (id: string | null) => void;
  } | null>;
};

const AvailableInCloudTooltip = ({
  description,
  link,
  children,
  tooltipId,
  TooltipContext,
}: Props) => {
  const context = useContext(TooltipContext);
  if (!context)
    throw new Error(
      'AvailableInCloudTooltip must be used within TooltipContext',
    );

  const { openTooltip, setOpenTooltip } = context;
  const open = openTooltip === tooltipId;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenTooltip(open ? null : tooltipId);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenTooltip(null)}>
      <div>
        <Tooltip
          open={open}
          disableHoverListener
          disableTouchListener
          disableFocusListener
          placement="right"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                padding: 0,
              },
            },
          }}
          title={<TooltipContent description={description} link={link} />}
        >
          <div onClick={handleClick}>{children}</div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

export default AvailableInCloudTooltip;
